import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Hallazgo de code review 2026-07-30 (10ma pasada, alcance ampliado a
// UX/seguridad/robustez/escalabilidad): el mecanismo anterior (EHR.js v1)
// solo reaccionaba al resultado de un refetch disparado por ESTE mismo
// código (onRecordSaved → query.refetch().then(...)) — pero react-query
// también puede disparar un refetch por su cuenta (refetchOnReconnect,
// refetchOnMount, ver hooks/useQuery.js) que nunca pasa por ahí. Ese fallo
// no mostraba el bloqueo de página completa (correcto, ya había datos) NI
// el toast (el guard nunca se ejecutaba) — quedaba completamente en
// silencio. Y EHR/V2/EHR.js directamente no tenía NINGÚN manejo de error.
//
// Este hook, compartido por v1 y v2, resuelve ambos casos a la vez:
// observa el ESTADO de la query (no quién disparó el refetch), así que
// cualquier fuente de refetch fallido queda cubierta por igual. Evita el
// falso positivo de "error viejo ya cacheado" con un baseline por
// consumerID: la PRIMERA vez que se observa un consumerID nunca avisa (solo
// establece el punto de partida) — a partir de ahí, cualquier cambio de
// errorUpdatedAt con isError verdadero es necesariamente un fallo NUEVO
// desde que este componente empezó a mirar a ese paciente. Al ser un mapa
// por-consumerID (no un contador global), un vaivén A→B→A no pierde ni
// confunde el estado de A con el de B (bug real de un diseño anterior,
// basado en contador, ya corregido en rondas previas de este mismo
// mecanismo — ver EHR.test.js).
// Hallazgo de code review 2026-07-31 (11va pasada, escalabilidad —
// CONFIRMADO): sin un tope, un turno largo abriendo decenas/cientos de
// pacientes distintos en la misma pestaña (EHR no se remonta entre
// pacientes, ver comentario de arriba sobre A→B→A) dejaba crecer
// `lastSeenErrorUpdatedAtRef` para siempre. FIFO simple (no LRU real: no
// hace falta "refrescar" la posición de un paciente ya visitado, alcanza
// con no perder detección de A→B→A entre los últimos N pacientes, que
// cubre cualquier ida-y-vuelta realista dentro de un mismo turno).
const MAX_TRACKED_CONSUMERS = 50;

const useStaleDataToast = (consumerID, query) => {
  const { t } = useTranslation();
  const lastSeenErrorUpdatedAtRef = useRef({});
  const trackedOrderRef = useRef([]);

  useEffect(() => {
    const seen = lastSeenErrorUpdatedAtRef.current;
    const isFirstObservation = !(consumerID in seen);

    if (isFirstObservation) {
      seen[consumerID] = query.errorUpdatedAt;
      const order = trackedOrderRef.current;
      order.push(consumerID);
      if (order.length > MAX_TRACKED_CONSUMERS) delete seen[order.shift()];
      return;
    }

    if (query.isError && !query.isLoadingError && query.errorUpdatedAt !== seen[consumerID]) {
      seen[consumerID] = query.errorUpdatedAt;
      // Hallazgo de code review 2026-07-30 (10ma pasada, no resuelto a
      // propósito): Notify.js dedupea por `title` (uniqBy, se queda con el
      // PRIMERO) — dos fallos reales e independientes dentro de la misma
      // ventana de `duration` (6s) puede que el segundo quede descartado en
      // silencio si el primero sigue visible. Arreglarlo de raíz requiere
      // tocar Notify.js (dedupe compartido por TODA la app, con call sites
      // que dependen a propósito de ese comportamiento para no duplicar un
      // title:"Error" genérico) — fuera de proporción para un caso borde de
      // dos fallos de refetch separados por segundos. Mitigación aceptada:
      // el toast VIEJO (todavía visible) ya le muestra al profesional que
      // hay datos potencialmente desactualizados, aunque no cuente cuántas
      // veces pasó.
      window.notify({
        message: t("ehr:refetch_error", "No se pudo actualizar la historia clínica. Los datos mostrados pueden estar desactualizados."),
        type: "danger",
        title: t("ehr:refetch_error_title", "Historia clínica desactualizada"),
        duration: 6000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerID, query.isError, query.isLoadingError, query.errorUpdatedAt]);
};

export default useStaleDataToast;
